import { useCallback, useMemo } from 'react';
import { Check } from 'lucide-react';

/**
 * Caja siempre con fondo blanco; **solo el tilde** usa primary (#2173FF).
 * Borde azul en marcado; sin relleno azul en la caja.
 */

const sizeInnerClass = {
  small: 'h-4 w-4',
  medium: 'h-5 w-5',
  large: 'h-7 w-7',
};

/** Tamaño del icono `Check` (px) por escala */
const sizeCheckPx = {
  small: 10,
  medium: 13,
  large: 18,
};

/** Trazo un poco más marcado en tallas grandes (legibilidad) */
const checkStrokeBySize = {
  small: 2.25,
  medium: 2.5,
  large: 2.75,
};

function normalizeSize(size) {
  if (size === 'small' || size === 'medium' || size === 'large') return size;
  return 'medium';
}

/**
 * Checkbox accesible (`role="checkbox"`, Espacio/Enter).
 * **Marcado:** fondo blanco, borde #2173FF, tilde azul (no hay fondo azul en la caja).
 *
 * @param {object} props
 * @param {boolean} [props.checked=false]
 * @param {boolean} [props.disabled=false]
 * @param {'small'|'medium'|'large'} [props.size='medium'] — Caja interior 16 / 20 / 28px.
 * @param {(event: import('react').MouseEvent | import('react').KeyboardEvent) => void} [props.onChange]
 * @param {string} [props.iconColor] — Color del borde (marcado) y del tilde; sin marcar: solo borde.
 * @param {string} [props.className]
 * @param {string} [props.id]
 * @param {string} [props['aria-label']]
 */
const Checkbox = ({
  checked = false,
  disabled = false,
  size = 'medium',
  onChange,
  iconColor,
  className = '',
  id,
  'aria-label': ariaLabel,
}) => {
  const s = normalizeSize(size);

  const handleClick = useCallback(
    (e) => {
      if (disabled) return;
      onChange?.(e);
    },
    [disabled, onChange],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (disabled) return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        onChange?.(e);
      }
    },
    [disabled, onChange],
  );

  const buttonStateClasses = disabled
    ? ''
    : checked
      ? 'hover:bg-[#E9F1FF] focus-visible:outline-none focus-visible:bg-[#80AFFF] focus-visible:ring-2 focus-visible:ring-[#80AFFF] focus-visible:ring-offset-2 dark:hover:bg-blue-950/40 dark:focus-visible:bg-blue-950/50'
      : 'hover:bg-[#F2F2F2] focus-visible:outline-none focus-visible:bg-[#D9D9D9] focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 dark:hover:bg-white/10 dark:focus-visible:bg-white/15';

  /** Marcado: borde + tilde. Vacío: solo borde. */
  const customTint = useMemo(() => {
    const raw = iconColor?.trim();
    if (!raw) return undefined;
    if (checked) return { borderColor: raw, color: raw };
    return { borderColor: raw };
  }, [iconColor, checked]);

  const innerClassName = useMemo(() => {
    const base =
      'inline-flex shrink-0 items-center justify-center rounded-sm border border-solid bg-surface transition-colors';

    if (customTint) {
      return [base, sizeInnerClass[s], 'border-2'].join(' ');
    }

    if (disabled) {
      if (checked) {
        return [
          base,
          sizeInnerClass[s],
          'border-2 border-[#80AFFF] text-[#80AFFF]',
        ].join(' ');
      }
      return [base, sizeInnerClass[s], 'border-[#BABABA]'].join(' ');
    }

    if (checked) {
      return [
        base,
        sizeInnerClass[s],
        'border-2 border-[#2173FF] text-[#2173FF] dark:border-blue-400 dark:text-blue-400',
      ].join(' ');
    }

    return [
      base,
      sizeInnerClass[s],
      'border-[#5C5C5C] dark:border-gray-400',
    ].join(' ');
  }, [s, checked, disabled, customTint]);

  return (
    <button
      id={id}
      type="button"
      role="checkbox"
      tabIndex={disabled ? -1 : 0}
      aria-checked={checked}
      aria-disabled={disabled}
      aria-label={ariaLabel}
      disabled={disabled}
      className={[
        'inline-flex shrink-0 items-center justify-center rounded-full border-none p-1 transition-colors duration-300 ease-out',
        buttonStateClasses,
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <span className={innerClassName} style={customTint}>
        {checked && (
          <Check
            size={sizeCheckPx[s]}
            strokeWidth={checkStrokeBySize[s]}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          />
        )}
      </span>
    </button>
  );
};

export default Checkbox;
